import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, MessageSquare, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { IconSelector } from '@/components/common/IconSelector';

const WORKING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const SettingsTab = ({
  formData, setFormData,
  personaTags, applyPersonaTag,
  setHasUnsavedChanges,
  bookingSettings, setBookingSettings,
  calendarConnections, loadingConnections,
  connectGoogleCalendar, disconnectCalendar,
  handleSyncFromCalendar, syncingCalendar, vacationBlocks,
}) => {
  return (
    <>
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-slate-900">Basic Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4">
            <div>
              <Label className="text-slate-600 mb-2 block">Icon & Color</Label>
              <IconSelector
                value={formData.icon}
                onChange={(v) => setFormData({ ...formData, icon: v })}
                color={formData.icon_color}
                onColorChange={(c) => setFormData({ ...formData, icon_color: c })}
              />
              <p className="text-xs text-slate-400 mt-1.5">Click to change</p>
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label className="text-slate-600">Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white border-slate-300 text-slate-900 mt-1" maxLength={60} />
              </div>
              <div>
                <Label className="text-slate-600">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-white border-slate-300 text-slate-900 resize-none mt-1" rows={2} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-slate-900">Conversation Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-600">Welcome Message</Label>
            <Textarea value={formData.welcome_message} onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })} className="bg-white border-slate-300 text-slate-900 resize-none mt-1" rows={2} placeholder="Hello! How can I help you today?" />
          </div>
        </CardContent>
      </Card>

      {/* Dedicated Personality card — mirrors the wizard's "Personality" step
          so the same "System Prompt" label appears in both surfaces. The
          previous single-card layout buried the prompt inside "Conversation
          Settings" and made users think the label was missing. */}
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="personality-card">
        <CardHeader>
          <CardTitle className="text-slate-900">Personality</CardTitle>
          <CardDescription className="text-slate-500">
            Define how your AI Employee speaks, its tone, and any rules it should follow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-700 font-medium" htmlFor="system-prompt-textarea">System Prompt</Label>
              <div className="text-xs text-slate-500">Quick presets:</div>
            </div>

            {/* Persona Tags */}
            <div className="flex flex-wrap gap-2 mb-3">
              {personaTags.map((tag) => (
                <Button
                  key={tag.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyPersonaTag(tag)}
                  className="text-xs h-7 border-slate-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
                  data-testid={`persona-preset-${tag.id}`}
                >
                  {tag.name}
                </Button>
              ))}
            </div>

            <Textarea
              id="system-prompt-textarea"
              value={formData.system_prompt}
              onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
              className="bg-white border-slate-300 text-slate-900 resize-none mt-1"
              rows={6}
              placeholder="Describe your AI assistant's personality, tone, and expertise..."
              data-testid="system-prompt-textarea"
            />
            <p className="text-xs text-slate-500 mt-2">
              Define how your AI agent behaves. Click a preset above or customize your own. Key traits: cite sources naturally, use clear formatting (paragraphs/points), collect lead info conversationally.
            </p>
          </div>

          {/* AI Response Tone */}
          <div>
            <Label className="text-slate-700 font-medium">Response Tone</Label>
            <p className="text-xs text-slate-500 mb-3">Control how formal or casual your AI agent sounds</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Professional</span><span>Balanced</span><span>Friendly</span>
              </div>
              <Slider
                value={[formData.response_tone === 'professional' ? 0 : formData.response_tone === 'friendly' ? 100 : 50]}
                onValueChange={([val]) => {
                  const tone = val <= 25 ? 'professional' : val >= 75 ? 'friendly' : 'balanced';
                  setFormData({ ...formData, response_tone: tone });
                }}
                max={100}
                step={50}
                className="w-full"
                data-testid="response-tone-slider"
              />
              <div className="flex items-center justify-center">
                <Badge className={
                  formData.response_tone === 'professional' ? 'bg-slate-100 text-slate-700 border-slate-200' :
                  formData.response_tone === 'friendly' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                  'bg-blue-100 text-blue-700 border-blue-200'
                }>
                  {formData.response_tone === 'professional' ? 'Professional & Formal' :
                   formData.response_tone === 'friendly' ? 'Warm & Friendly' :
                   'Balanced & Approachable'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Follow-up Questions Toggle */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <Label className="text-slate-800 font-medium">Follow-up Questions</Label>
                <p className="text-sm text-slate-600">
                  AI will ask small questions to keep conversation flowing and gather lead info naturally
                </p>
              </div>
            </div>
            <Switch
              checked={formData.enable_followup_questions}
              onCheckedChange={(checked) => {
                setFormData({ ...formData, enable_followup_questions: checked });
                setHasUnsavedChanges(true);
              }}
              className="data-[state=checked]:bg-green-600"
              data-testid="enable-followup-questions-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Calendar & Booking Settings */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Appointment Booking
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                Allow visitors to schedule appointments through this AI agent
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-slate-600 text-sm">Enable</Label>
              <Switch
                checked={bookingSettings.enabled}
                onCheckedChange={(checked) => setBookingSettings({ ...bookingSettings, enabled: checked })}
                className="data-[state=checked]:bg-blue-600"
                data-testid="enable-booking-switch"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Calendar Connection */}
          <div>
            <Label className="text-slate-600">Connected Calendar</Label>
            {loadingConnections ? (
              <div className="flex items-center gap-2 mt-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading calendars...</span>
              </div>
            ) : calendarConnections.length === 0 ? (
              <div className="mt-2 p-4 rounded-lg border border-dashed border-slate-300">
                <div className="text-center mb-4">
                  <Calendar className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">No calendars connected</p>
                  <p className="text-xs text-slate-500 mt-1">Connect a calendar so your AI agent can manage appointments</p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <Button onClick={connectGoogleCalendar} variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 border-slate-300 hover:bg-blue-50 hover:border-blue-300" data-testid="connect-google-calendar">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-sm font-medium text-slate-700">Connect Google Calendar</span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Select
                  value={bookingSettings.calendar_connection_id}
                  onValueChange={(v) => setBookingSettings({ ...bookingSettings, calendar_connection_id: v })}
                >
                  <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-1">
                    <SelectValue placeholder="Select a calendar" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {calendarConnections.map((conn) => (
                      <SelectItem key={conn.connection_id} value={conn.connection_id} className="text-slate-700">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${conn.provider === 'google' ? 'bg-red-500' : 'bg-slate-400'}`}></span>
                          {conn.calendar_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 mt-2">
                  <Button onClick={connectGoogleCalendar} variant="outline" size="sm" className="border-slate-300 text-slate-700">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Add Another Calendar
                  </Button>
                  {bookingSettings.calendar_connection_id && (
                    <Button
                      onClick={() => disconnectCalendar(bookingSettings.calendar_connection_id)}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {bookingSettings.enabled && (
            <>
              {/* Time Slot Settings */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Slot Duration</Label>
                  <Select
                    value={bookingSettings.slot_duration_minutes.toString()}
                    onValueChange={(v) => setBookingSettings({ ...bookingSettings, slot_duration_minutes: parseInt(v) })}
                  >
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="15" className="text-slate-700">15 minutes</SelectItem>
                      <SelectItem value="30" className="text-slate-700">30 minutes</SelectItem>
                      <SelectItem value="45" className="text-slate-700">45 minutes</SelectItem>
                      <SelectItem value="60" className="text-slate-700">1 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Buffer Between Meetings</Label>
                  <Select
                    value={bookingSettings.buffer_minutes.toString()}
                    onValueChange={(v) => setBookingSettings({ ...bookingSettings, buffer_minutes: parseInt(v) })}
                  >
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="0" className="text-slate-700">No buffer</SelectItem>
                      <SelectItem value="5" className="text-slate-700">5 minutes</SelectItem>
                      <SelectItem value="15" className="text-slate-700">15 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sync from Google Calendar */}
              {calendarConnections.some(c => c.provider === 'google') && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Auto-detect Schedule from Google Calendar</p>
                      <p className="text-xs text-blue-700 mt-0.5">Pulls your working hours, timezone, and vacation/OOO events directly from your connected calendar.</p>
                    </div>
                    <Button
                      variant="outline" size="sm"
                      onClick={handleSyncFromCalendar}
                      disabled={syncingCalendar}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100 shrink-0 ml-4"
                      data-testid="sync-calendar-btn"
                    >
                      {syncingCalendar ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Syncing...</> : <><Calendar className="w-3 h-3 mr-1.5" />Sync Now</>}
                    </Button>
                  </div>
                  {vacationBlocks.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <p className="text-xs font-medium text-blue-800">Detected OOO / Vacation blocks ({vacationBlocks.length}):</p>
                      {vacationBlocks.map((v, i) => (
                        <div key={`vac-${v.start_date}-${i}`} className="flex items-center gap-2 text-xs text-blue-700 bg-blue-100 rounded px-2 py-1">
                          <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" />
                          <span className="font-medium">{v.summary}</span>
                          <span className="text-blue-500">{v.start_date} → {v.end_date}</span>
                        </div>
                      ))}
                      <p className="text-xs text-blue-600 mt-1">These dates are automatically blocked from booking.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Working Hours */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Working Hours Start</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <Input
                      type="time"
                      value={bookingSettings.working_hours_start}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, working_hours_start: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600">Working Hours End</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <Input
                      type="time"
                      value={bookingSettings.working_hours_end}
                      onChange={(e) => setBookingSettings({ ...bookingSettings, working_hours_end: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900"
                    />
                  </div>
                </div>
              </div>

              {/* Working Days */}
              <div>
                <Label className="text-slate-600">Working Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {WORKING_DAYS.map((day, index) => (
                    <button
                      key={day}
                      onClick={() => {
                        const newDays = bookingSettings.working_days.includes(index)
                          ? bookingSettings.working_days.filter(d => d !== index)
                          : [...bookingSettings.working_days, index].sort();
                        setBookingSettings({ ...bookingSettings, working_days: newDays });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        bookingSettings.working_days.includes(index)
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Booking Window */}
              <div>
                <Label className="text-slate-600">Booking Window (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={90}
                  value={bookingSettings.advance_days}
                  onChange={(e) => setBookingSettings({ ...bookingSettings, advance_days: parseInt(e.target.value) || 14 })}
                  className="bg-white border-slate-300 text-slate-900 mt-1 w-32"
                />
              </div>

              {/* Info Box */}
              {!bookingSettings.calendar_connection_id && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-700">
                    <strong>Note:</strong> Please select a connected calendar to enable appointment booking.
                  </p>
                </div>
              )}

              {bookingSettings.calendar_connection_id && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-700">
                    <strong>Booking enabled!</strong> Visitors can schedule appointments through this chatbot.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default SettingsTab;
